// Form submission against the API.
//
// The server reports every bad field at once in a details array. This keeps that
// shape intact so a form can mark all its bad inputs in one pass rather than
// making the user fix them one at a time.

import { useCallback, useState } from 'react'
import { ApiError } from './api.js'

// The server rejects a blank string where it wants a number or a date, so
// empty optional form fields go over the wire as null. Callers re-assert
// their required fields afterwards so a blank required field still reaches
// zod as a string and gets a proper field error.
export function blankToNull(form) {
  return Object.fromEntries(
    Object.entries(form).map(([key, value]) => [key, value === '' ? null : value]),
  )
}

export function useApiForm() {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  // Runs the request, routes any failure to the right place, and returns the
  // result on success or undefined on failure. Callers branch on that.
  const submit = useCallback(async (request) => {
    setSaving(true)
    setError(null)
    setFieldErrors({})
    try {
      return await request()
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray(caught.details)) {
        const byField = {}
        for (const detail of caught.details) {
          if (detail.field && !byField[detail.field]) byField[detail.field] = detail.message
        }
        setFieldErrors(byField)
      }
      setError(caught.message || 'Something went wrong. Please try again.')
      return undefined
    } finally {
      setSaving(false)
    }
  }, [])

  return { saving, error, fieldErrors, submit }
}
